import { useId } from 'react';
import type { ReactNode } from 'react';
import { Rich } from '../../bits';
import { useT } from '../../../messages';
import type { ThemeContract, ZoneReport } from '../../../wire';

/**
 * **The custom stylesheet, and the rules for a content zone** — the last rung
 * of the customization ladder, moved into a drawer from the old editor
 * unchanged in substance: the byte budget counted the way the server counts
 * it, the version the stylesheet was pinned to, and the sanitizer's own
 * allowlist, with how many zones it touched on the last publish.
 */
export function CssPanel({
  contract,
  value,
  pinnedTo,
  zones,
  onChange,
}: {
  contract: ThemeContract;
  value: string;
  pinnedTo: number | null;
  zones: readonly ZoneReport[];
  onChange: (value: string) => void;
}): ReactNode {
  const t = useT();
  const id = useId();
  const bytes = new TextEncoder().encode(value).length;
  const limit = contract.limits.custom_css_bytes;
  const touched = zones.filter((z) => z.changed).length;

  return (
    <div className="bld-stack">
      <section className="bld-ins-group">
        <p className="bld-hint">
          <Rich text={t('branding.cssHint')} />
        </p>
        <div className="bld-field">
          <label htmlFor={id}>{t('branding.css')}</label>
          <textarea
            id={id}
            className="bld-input bld-mono"
            rows={12}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
          {/* Ungrouped on purpose: a measurement of the box against the limit it is held to. */}
          <span className={bytes > limit ? 'error-text' : 'bld-hint'}>
            {t('branding.cssBytes', { bytes, limit })}
            {pinnedTo === null ? '' : t('branding.cssPinned', { version: pinnedTo })}
          </span>
        </div>
        <p className="bld-hint">
          <Rich text={t('branding.cssNotYet')} />
        </p>
      </section>

      <section className="bld-ins-group">
        <h4 className="bld-label">
          {t('branding.sanitizer')}
          {touched === 0 ? null : (
            <span className="bld-mark bld-mark--bad">
              {t('branding.sanitized', { count: touched })}
            </span>
          )}
        </h4>
        <p className="bld-hint">{contract.sanitizer.note}</p>
        <p>
          {t('branding.allowed')}{' '}
          {contract.sanitizer.allowed_tags.map((tag) => (
            <code key={tag}>{`<${tag}> `}</code>
          ))}
        </p>
        <p className="bld-hint">
          {t('branding.linkSchemes', { schemes: contract.sanitizer.allowed_link_schemes })}
        </p>
      </section>
    </div>
  );
}
